import React, { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { getSupportDomain } from '@renderer/utils/brandUtils'
import { Button } from '@renderer/components'
import { FaCamera, FaCheck } from 'react-icons/fa'
import { BackgroundContainer, GlassPanel } from './BackgroundContainer'
import { useDesignLayout } from '@renderer/hooks'

interface ConsentModalProps {
  onConsent: (consent: boolean) => void
  isVisible: boolean
  stripPreview?: string
  mode?: 'capture' | 'record'
  timeout?: number // Optional timeout in seconds, defaults to 30
}

const ConsentModal: React.FC<ConsentModalProps> = ({
  onConsent,
  isVisible,
  stripPreview,
  mode,
  timeout = 30
}) => {
  const { t } = useTranslation()
  const { isCompact, isLandscape, isSpacious } = useDesignLayout()
  const consentModalRowLayout = isLandscape
  const consentLandscapeShort = isCompact && isLandscape
  const consentContentJustify = consentModalRowLayout ? 'justify-center' : 'justify-start'
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect((): (() => void) => {
    if (isVisible && timeout > 0) {
      timeoutRef.current = setTimeout(() => {
        onConsent(false)
      }, timeout * 1000)
    }

    return (): void => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [isVisible, timeout, onConsent])

  if (!isVisible) return null

  const outerPad = isCompact ? 'p-2' : isSpacious ? 'p-4' : 'p-6'

  /** Hug content height on phones; cap + scroll when tall — avoid `h-full` stretching the glass panel */
  const glassPanelClass = isCompact
    ? 'h-auto w-full max-w-7xl max-h-[96dvh] overflow-y-auto'
    : consentModalRowLayout
      ? `h-auto w-full max-h-[92dvh] overflow-y-auto ${isSpacious ? 'max-w-[min(94vw,1400px)]' : 'max-w-4xl'}`
      : `h-auto w-full max-h-[94dvh] overflow-y-auto ${isSpacious ? 'max-w-[min(92vw,900px)]' : 'max-w-[min(90vw,640px)]'}`

  const stripPaneClass = consentModalRowLayout
    ? consentLandscapeShort
      ? 'max-h-full min-h-0 w-[38%] max-w-[40%] shrink-0 p-2'
      : isCompact
        ? 'max-h-full min-h-0 w-[38%] max-w-[40%] shrink-0 p-2'
        : isSpacious
          ? 'max-h-full min-h-0 w-[40%] max-w-[42%] shrink-0 p-6 sm:p-8'
          : 'max-h-full min-h-0 w-[340px] shrink-0 p-6'
    : isCompact
      ? 'w-full shrink-0 p-3'
      : isSpacious
        ? 'w-full shrink-0 p-6 sm:p-8'
        : 'max-h-[min(40dvh,440px)] min-h-[10rem] w-full shrink-0 p-6'

  /** Portrait: size strip by max width/height so the whole strip stays visible (photo-strip principle); no `h-full` fill */
  const stripMediaMax = consentModalRowLayout
    ? 'max-h-full max-w-full'
    : isCompact
      ? 'max-h-[min(36dvh,300px)] max-w-full'
      : isSpacious
        ? 'max-h-[min(62dvh,820px)] w-auto max-w-[min(88vw,560px)]'
        : 'max-h-[min(40dvh,440px)] max-w-full'

  const contentPadClass = !consentModalRowLayout && isCompact
    ? 'shrink-0 px-4 py-3'
    : consentLandscapeShort
      ? 'px-2 py-2'
      : isCompact
        ? 'px-4 py-3'
        : consentModalRowLayout
          ? isSpacious ? 'p-10 lg:p-12' : 'p-10'
          : isSpacious ? 'px-10 py-8 sm:px-12 sm:py-10' : 'px-8 py-6 sm:p-10'

  const headingClass = consentLandscapeShort
    ? 'mb-1 text-base leading-tight'
    : isCompact
      ? 'mb-2 text-xl'
      : isSpacious
        ? 'mb-4 text-4xl'
        : 'mb-4 text-3xl'

  const bodyClass = consentLandscapeShort
    ? 'text-xs leading-snug'
    : isCompact
      ? 'text-sm leading-relaxed'
      : isSpacious
        ? 'text-2xl leading-relaxed'
        : 'text-xl leading-relaxed'

  const sectionGap = consentLandscapeShort
    ? 'mb-2'
    : isCompact
      ? 'mb-4'
      : isSpacious
        ? 'mb-10'
        : 'mb-8'

  const benefitSpacing = consentLandscapeShort ? 'space-y-1' : isCompact ? 'space-y-2' : isSpacious ? 'space-y-5' : 'space-y-4'
  const benefitGap = consentLandscapeShort ? 'gap-1.5' : isCompact ? 'gap-2' : isSpacious ? 'gap-4' : 'gap-4'
  const benefitIconPad = consentLandscapeShort ? 'p-0.5' : isCompact ? 'p-1' : isSpacious ? 'p-2.5 mt-1' : 'p-2 mt-1'
  const benefitIconSize = consentLandscapeShort || isCompact ? 'text-xs' : isSpacious ? 'text-base' : 'text-sm'
  const benefitTextClass = consentLandscapeShort
    ? 'text-[11px] leading-tight'
    : isCompact
      ? 'text-sm'
      : isSpacious
        ? 'text-lg'
        : 'text-base'

  const buttonGap = consentLandscapeShort ? 'gap-1.5' : isCompact ? 'gap-2' : isSpacious ? 'gap-5' : 'gap-4'
  const buttonPad = consentLandscapeShort
    ? '!py-1.5 !text-sm'
    : isCompact
      ? '!py-2 !text-base'
      : isSpacious
        ? '!py-5 !text-xl'
        : '!py-4 !text-lg'

  const footerGap = consentLandscapeShort ? 'mt-2' : isCompact ? 'mt-3' : isSpacious ? 'mt-8' : 'mt-6'
  const footerTextClass = consentLandscapeShort
    ? 'text-[10px] leading-snug'
    : isCompact
      ? 'text-xs'
      : isSpacious
        ? 'text-base'
        : 'text-sm'

  return (
    <BackgroundContainer className={`fixed inset-0 z-50 flex items-center justify-center ${outerPad}`}>
      <GlassPanel className={`relative z-10 mx-auto flex flex-col rounded-3xl ${glassPanelClass}`}>
        <div
          className={`flex w-full min-h-0 ${consentModalRowLayout ? 'min-h-0 flex-row gap-1' : 'flex-col'}`}
        >
          {/* Strip preview */}
          <div
            className={`flex min-h-0 min-w-0 items-center justify-center ${consentModalRowLayout ? 'overflow-hidden' : 'overflow-visible'} ${stripPaneClass}`}
          >
            {stripPreview ? (
              <div className="flex w-full min-w-0 items-center justify-center">
                {mode === 'record' ? (
                  <video
                    src={stripPreview}
                    autoPlay
                    loop
                    muted
                    playsInline
                    className={`h-auto w-auto object-contain object-center ${stripMediaMax}`}
                  />
                ) : (
                  <img
                    src={stripPreview}
                    alt={t('consent.photoStripAlt', 'Photo Strip')}
                    className={`h-auto w-auto object-contain object-center ${stripMediaMax}`}
                  />
                )}
              </div>
            ) : (
              <div
                className={`flex w-full items-center justify-center ${isCompact ? 'h-full min-h-[8rem]' : 'h-48 max-w-sm'}`}
              >
                <FaCamera className={`text-gray-400 ${isCompact ? 'text-4xl' : isSpacious ? 'text-7xl' : 'text-6xl'}`} />
              </div>
            )}
          </div>

          {/* Content pane */}
          <div
            className={`custom-scrollbar flex min-h-0 min-w-0 flex-col overflow-y-auto overflow-x-hidden ${consentModalRowLayout ? 'flex-1' : 'w-full shrink-0'} ${consentContentJustify} ${contentPadClass}`}
          >
            <div className={`text-center ${sectionGap}`}>
              <h1 className={`font-bold text-gray-800 ${headingClass}`}>
                {t('consent.title', 'Photo Publication Consent')}
              </h1>
              <p className={`text-gray-600 ${bodyClass}`}>
                {t('consent.question', 'Can we use your photo strip for publication?')}
              </p>
            </div>

            <div className={sectionGap}>
              <div className={benefitSpacing}>
                <div className={`flex items-start ${benefitGap}`}>
                  <div className={`bg-blue-100 rounded-full shrink-0 mt-0.5 ${benefitIconPad}`}>
                    <FaCheck className={`text-blue-600 ${benefitIconSize}`} />
                  </div>
                  <span className={`min-w-0 break-words text-gray-700 ${benefitTextClass}`}>
                    {t('consent.benefits.featured', 'Featured on our platforms')}
                  </span>
                </div>
                <div className={`flex items-start ${benefitGap}`}>
                  <div className={`bg-blue-100 rounded-full shrink-0 mt-0.5 ${benefitIconPad}`}>
                    <FaCheck className={`text-blue-600 ${benefitIconSize}`} />
                  </div>
                  <span className={`min-w-0 break-words text-gray-700 ${benefitTextClass}`}>
                    {t('consent.benefits.inspire', 'Inspire others')}
                  </span>
                </div>
                <div className={`flex items-start ${benefitGap}`}>
                  <div className={`bg-blue-100 rounded-full shrink-0 mt-0.5 ${benefitIconPad}`}>
                    <FaCheck className={`text-blue-600 ${benefitIconSize}`} />
                  </div>
                  <span className={`min-w-0 break-words text-gray-700 ${benefitTextClass}`}>
                    {t('consent.benefits.privacy', 'Privacy protected')}
                  </span>
                </div>
              </div>
            </div>

            <div className={`flex flex-col ${buttonGap}`}>
              <Button
                text={t('consent.accept', "Yes, I'd love to!")}
                onClick={() => onConsent(true)}
                variant="primary"
                className={`w-full font-semibold ${buttonPad}`}
              />
              <Button
                text={t('consent.decline', 'No, thanks')}
                onClick={() => onConsent(false)}
                variant="secondary"
                className={`w-full font-semibold ${buttonPad}`}
              />
            </div>

            <div className={`text-center ${footerGap}`}>
              <p className={`break-words text-gray-500 ${footerTextClass}`}>
                {t(
                  'consent.footer',
                  `You can change this preference anytime by contacting our support team at ${getSupportDomain()}.`
                )}
              </p>
            </div>
          </div>
        </div>
      </GlassPanel>
    </BackgroundContainer>
  )
}

export default ConsentModal
